import React, { memo, useState, useEffect, useReducer } from 'react';
import { get } from 'lodash';
import PropTypes from 'prop-types';

import { enhance } from './enhance';
import Fieldset from '../Fieldset';
import Loader from '../../common/Loader';
import EditBtn from '../../Button/EditBtn';
import InsurancesInfo from '../../InsurancesInfo';
import InsuranceDetailsForm from './InsuranceDetailsForm';
import UserDocumentsPreview from './UserDocumentsPreview';

import { INSURANCE_FLOW_TYPE } from '../../../constants/insurance-flow-type';
import useGetPatientInsuranceData from '../../../hooks/patientInsurance/useGetPatientInsuranceData';

import { getDriverLicenseDocumentFieldset } from '../utils/getFieldset';

import styles from './styles.styl';

const FLOW_TYPE = INSURANCE_FLOW_TYPE.PROFILE;

function InsuranceDetails({
  canEdit,
  checkInsuranceLoading,
  clearInsuranceRelatedData,
  deleteDocumentItem,
  deleteDocuments,
  driverLicenseDocuments,
  intl: { formatMessage },
  targetUserId,
  targetUserName,
  updateDocuments,
  uploadDocuments,
}) {
  const [edit, toggleEdit] = useReducer(confirm => !confirm, false);

  const [isEditMode, setIsEditMode] = useState(false);

  const {
    insuranceData,
    isInsuranceRelatedDataFetching,
  } = useGetPatientInsuranceData({
    userId: targetUserId,
    flowType: FLOW_TYPE,
    isInsuranceEditOpen: edit,
  });

  const showInsuranceInfoData = !edit && !isInsuranceRelatedDataFetching;

  const showInsuranceForm = edit && !isInsuranceRelatedDataFetching;

  const handleEditModeChange = editMode => {
    setIsEditMode(editMode);
  };

  useEffect(
    () => () => {
      clearInsuranceRelatedData();
    },
    [],
  );

  const handleSave = fields => {
    fields.map(field => {
      const key = get(field, 'fieldName');
      const id = get(field, 'id');
      const file = get(field, 'value.file');
      const deleted = get(field, 'value.deleted');
      const data = new FormData();
      data.append('attachment', file);
      data.append('userId', targetUserId);

      if (deleted && id) {
        deleteDocumentItem({ id });
        deleteDocuments({ userId: targetUserId, id });
      }
      if (file && id) {
        data.append('id', id);
        deleteDocumentItem({ id });
        updateDocuments(data);
      }
      if (file && !id) {
        data.append('key', key);

        uploadDocuments(data);
      }
      return false;
    });
  };

  return (
    <div className={styles.root}>
      <div className={styles.titleWrapper}>
        <h3 className={styles.title}>
          {formatMessage({ id: 'profile.section.insurance' })}
        </h3>
        <div className={styles.editButton}>
          <EditBtn
            onClick={toggleEdit}
            disabled={isInsuranceRelatedDataFetching || checkInsuranceLoading}
          />
        </div>
      </div>
      {isInsuranceRelatedDataFetching && <Loader />}

      {showInsuranceInfoData && (
        <InsurancesInfo
          flowType={FLOW_TYPE}
          userId={targetUserId}
          userName={targetUserName}
          withSelfPayEnding
        />
      )}

      {showInsuranceForm && (
        <InsuranceDetailsForm
          cancel={toggleEdit}
          flowType={FLOW_TYPE}
          insuranceData={insuranceData}
          targetUserName={targetUserName}
        />
      )}

      <div className={styles.profileDocWrapper}>
        <Fieldset
          fieldset={getDriverLicenseDocumentFieldset(
            formatMessage,
            driverLicenseDocuments,
            isEditMode,
          )}
          onSave={handleSave}
          disabled={!canEdit}
          canEdit={canEdit}
          onEditModeChange={handleEditModeChange}
        />
      </div>

      <UserDocumentsPreview documents={driverLicenseDocuments} />
    </div>
  );
}

InsuranceDetails.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
  canEdit: PropTypes.bool,
  checkInsuranceLoading: PropTypes.bool,
  clearInsuranceRelatedData: PropTypes.func,
  deleteDocumentItem: PropTypes.func,
  deleteDocuments: PropTypes.func,
  driverLicenseDocuments: PropTypes.array,
  formatMessage: PropTypes.func,
  targetUserId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  targetUserName: PropTypes.string,
  updateDocuments: PropTypes.func,
  uploadDocuments: PropTypes.func,
};

export default memo(enhance(InsuranceDetails));



// WEBPACK FOOTER //
// ./src/components/ProfileLayout/InsuranceDetails/index.jsx